/**
 * «Cambios sin guardar»: quién tiene trabajo sin guardar y cómo guardarlo, y las reglas
 * de la guarda que pregunta antes de salir (`GuardaDeNavegacion.tsx`).
 *
 * Está fuera de React a propósito. La guarda tiene que decidir **en el momento** en que
 * alguien navega —un clic en el menú, un `navigate()` del selector de webs, el botón
 * «atrás»— y a esa altura un estado de React puede estar un render por detrás: el editor
 * que acaba de guardar y navega en la misma línea todavía se vería «sucio». Aquí se lee
 * siempre lo último.
 *
 * Todo lo de este fichero es puro y se prueba sin navegador (`guarda.test.ts`).
 */
export type Fuente = {
  /** Si ahora mismo hay algo sin guardar. Se pregunta cada vez: no se cachea. */
  hayCambios: () => boolean
  /** Guardarlo. `true` si quedó guardado; `false` si no (el editor ya enseña por qué). */
  guardar: () => Promise<boolean>
}

const fuentes = new Set<Fuente>()

/**
 * Mientras se sale «sin guardar» (o después de guardar), la navegación que sigue no se
 * vuelve a preguntar. Se apaga en cuanto cambia la ubicación (`yaSeSalio`).
 */
let dejandoPasar = false

/** Registra un editor. Devuelve la función que lo quita, para el `useEffect`. */
export const registrarCambios = (fuente: Fuente): (() => void) => {
  fuentes.add(fuente)
  return () => {
    fuentes.delete(fuente)
  }
}

export const hayCambiosSinGuardar = (): boolean => !dejandoPasar && [...fuentes].some((f) => f.hayCambios())

/** La navegación que se dejó pasar ya ha llegado: la guarda vuelve a mirar. */
export const yaSeSalio = (): void => {
  dejandoPasar = false
}

/** Guarda todo lo que tenga cambios, en orden; al primer fallo para y dice que no. */
export const guardarLosCambios = async (): Promise<boolean> => {
  for (const f of [...fuentes]) if (f.hayCambios() && !(await f.guardar())) return false
  return true
}

/** Para las pruebas: vacía el registro. */
export const olvidarTodo = (): void => {
  fuentes.clear()
  dejandoPasar = false
  pregunta = null
}

// --- Qué navegaciones se paran -------------------------------------------------------------

/**
 * Rutas a las que se deja ir sin preguntar. `/entrar`: si la sesión se ha perdido, guardar
 * ya no es posible (el núcleo responde 401) y preguntar solo dejaría al usuario en una
 * pantalla que no funciona.
 */
const SIN_PREGUNTAR = ['/entrar']

/** Quita lo que no cambia de página: solo el hash. */
const sinHash = (ruta: string): string => ruta.split('#')[0] ?? ''

/**
 * Si ir de `actual` a `destino` hay que preguntarlo. No se pregunta al quedarse en la misma
 * página (solo cambia el `#`), ni al ir a donde no se puede guardar.
 */
export const hayQuePreguntar = (actual: string, destino: string): boolean => {
  if (sinHash(actual) === sinHash(destino)) return false
  const ruta = sinHash(destino).split('?')[0] ?? ''
  return !SIN_PREGUNTAR.some((r) => ruta === r || ruta.startsWith(`${r}/`))
}

export type Clic = {
  /** El `href` del enlace, ya resuelto por el navegador (`anchor.href`). */
  href: string
  target: string
  descarga: boolean
  boton: number
  teclas: { ctrl: boolean; meta: boolean; shift: boolean; alt: boolean }
  /** Ya lo canceló otro (`preventDefault`). */
  cancelado: boolean
}

/**
 * De un clic en un enlace, la ruta interna a la que llevaría (`/e/1/s/2/paginas?x=1#y`), o
 * `null` si el clic no es una navegación interna de esta pestaña: botón central, con Ctrl o
 * Cmd (se abre en otra pestaña y aquí no se pierde nada), `target="_blank"`, una descarga u
 * otro origen.
 */
export const destinoDelClic = (clic: Clic, origen: string): null | string => {
  if (clic.cancelado || clic.boton !== 0) return null
  if (clic.teclas.ctrl || clic.teclas.meta || clic.teclas.shift || clic.teclas.alt) return null
  if (clic.descarga || (clic.target && clic.target !== '_self')) return null
  let url: URL
  try {
    url = new URL(clic.href)
  } catch {
    return null
  }
  if (url.origin !== origen) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Cuántos pasos se movió el historial en un «atrás» o «adelante»: el enrutador apunta en
 * `history.state.idx` la posición de cada entrada. Sin ella (una entrada que no es suya),
 * se supone un paso atrás, que es lo corriente.
 */
export const pasosDelHistorial = (antes: null | number, despues: unknown): number => {
  const idx = despues && typeof despues === 'object' ? (despues as { idx?: unknown }).idx : undefined
  if (typeof idx !== 'number' || antes === null) return -1
  return idx - antes
}

/** A qué ruta lleva lo que se le pasa al navegador del enrutador: una cadena o `{ pathname, search, hash }`. */
export const rutaDe = (a: unknown): string => {
  if (typeof a === 'string') return a
  if (a && typeof a === 'object') {
    const p = a as { pathname?: unknown; search?: unknown; hash?: unknown }
    return `${typeof p.pathname === 'string' ? p.pathname : ''}${typeof p.search === 'string' ? p.search : ''}${typeof p.hash === 'string' ? p.hash : ''}`
  }
  return ''
}

/** Lo que el enrutador usa para moverse (`navigator` de React Router: el `history` del navegador). */
export type Navegador = { push: (...args: never[]) => unknown; replace: (...args: never[]) => unknown; go: (pasos: number) => unknown }

/**
 * «`navigate` envuelto»: toda navegación del enrutador —un `<Link>`, un `navigate()` desde
 * cualquier pantalla, el selector de webs del menú— pasa por aquí mientras haya un editor
 * abierto. Si hay cambios sin guardar y la navegación cambia de página, se pregunta; si
 * no, sigue tal cual. Devuelve cómo dejarlo como estaba.
 */
export const envolverNavegador = (
  navegador: Navegador,
  o: { actual: () => string; preguntarYSeguir: (destino: string, seguir: () => void) => void; hayCambios?: () => boolean },
): (() => void) => {
  const original = { push: navegador.push, replace: navegador.replace, go: navegador.go }
  const hay = o.hayCambios ?? hayCambiosSinGuardar
  const envolver =
    (clave: 'push' | 'replace') =>
    (...args: never[]) => {
      const seguir = () => original[clave].apply(navegador, args)
      const destino = rutaDe(args[0])
      if (hay() && hayQuePreguntar(o.actual(), destino)) o.preguntarYSeguir(destino, seguir)
      else seguir()
    }
  navegador.push = envolver('push')
  navegador.replace = envolver('replace')
  navegador.go = (pasos: number) => {
    const seguir = () => original.go.call(navegador, pasos)
    if (pasos !== 0 && hay()) o.preguntarYSeguir('', seguir)
    else seguir()
  }
  return () => {
    navegador.push = original.push
    navegador.replace = original.replace
    navegador.go = original.go
  }
}

// --- Lo que se pregunta ------------------------------------------------------------------

/** «salir» también es lo que se responde después de guardar bien: el diálogo guarda y, si sale, sigue. */
export type Respuesta = 'quedarme' | 'salir'

export type Pregunta = { destino: string; responder: (r: Respuesta) => void }

let pregunta: null | Pregunta = null
const oyentes = new Set<() => void>()
const avisar = () => {
  for (const o of oyentes) o()
}

/** La pregunta en curso, para `useSyncExternalStore`. */
export const preguntaEnCurso = (): null | Pregunta => pregunta
export const escucharPregunta = (oyente: () => void): (() => void) => {
  oyentes.add(oyente)
  return () => {
    oyentes.delete(oyente)
  }
}

/**
 * Pregunta qué hacer antes de ir a `destino`. Si ya hay una pregunta abierta, la nueva
 * la sustituye (el último clic es el que cuenta) y la anterior se da por «quedarme».
 */
export const preguntar = (destino: string): Promise<Respuesta> =>
  new Promise((resolver) => {
    pregunta?.responder('quedarme')
    const esta: Pregunta = {
      destino,
      responder: (r) => {
        if (pregunta === esta) {
          pregunta = null
          avisar()
        }
        resolver(r)
      },
    }
    pregunta = esta
    avisar()
  })

/**
 * Lo que hace la guarda con una navegación que habría que preguntar: pregunta y, si la
 * respuesta es salir (sin guardar, o después de guardar bien), sigue con la navegación
 * original **sin volver a preguntar**. `seguir` se llama como mucho una vez.
 */
export const decidir = async (destino: string, seguir: () => void): Promise<Respuesta> => {
  const respuesta = await preguntar(destino)
  if (respuesta === 'salir') {
    dejandoPasar = true
    seguir()
  }
  return respuesta
}
